import { useEffect, useState } from 'react';
import { api } from '@/api';
import type { SafeUser } from '@ultisim/shared';
import { getInitials } from '@ultisim/shared';
import { useAuthStore } from '@/stores/auth';

type Tab = 'active' | 'inactive';

export default function AdminUsersPage() {
  const { user: currentUser } = useAuthStore();
  const canSeeSalary = !!(currentUser as (SafeUser & { canManagePayroll?: boolean }) | null)?.canManagePayroll;
  const [users, setUsers] = useState<SafeUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', firstName: '', lastName: '', password: '', role: 'employee' });
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<Tab>('active');
  const [resetConfirm, setResetConfirm] = useState<SafeUser | null>(null);
  const [resetSending, setResetSending] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [setPasswordUser, setSetPasswordUser] = useState<SafeUser | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [settingPassword, setSettingPassword] = useState(false);
  // Salary editing
  const [salaryUser, setSalaryUser] = useState<SafeUser | null>(null);
  const [salaryInput, setSalaryInput] = useState('');
  const [savingSalary, setSavingSalary] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  // Auto-dismiss success message
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const loadUsers = async () => {
    try {
      const data = await api.get<SafeUser[]>('/users');
      setUsers(data);
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const createUser = async () => {
    setError('');
    try {
      await api.post('/users', newUser);
      setNewUser({ email: '', firstName: '', lastName: '', password: '', role: 'employee' });
      setIsAddingUser(false);
      loadUsers();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create user';
      setError(message);
    }
  };

  const toggleUserStatus = async (user: SafeUser) => {
    try {
      if (user.status === 'active') {
        await api.delete(`/users/${user.id}`);
      } else {
        await api.put(`/users/${user.id}`, { status: 'active' });
      }
      loadUsers();
    } catch (err) {
      console.error('Failed to update user:', err);
    }
  };

  const sendResetEmail = async (user: SafeUser) => {
    setResetSending(true);
    try {
      await api.post(`/users/${user.id}/reset-password`);
      setResetConfirm(null);
      setSuccessMessage(`Password reset link sent to ${user.email}`);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to send reset email';
      setError(message);
      setResetConfirm(null);
    } finally {
      setResetSending(false);
    }
  };

  const setPassword = async (user: SafeUser) => {
    if (!newPassword || newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setSettingPassword(true);
    try {
      await api.post(`/users/${user.id}/set-password`, { password: newPassword });
      setSetPasswordUser(null);
      setNewPassword('');
      setSuccessMessage(`Password set for ${user.firstName} ${user.lastName}. They will need to log in again.`);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to set password';
      setError(message);
      setSetPasswordUser(null);
      setNewPassword('');
    } finally {
      setSettingPassword(false);
    }
  };

  const saveSalary = async () => {
    if (!salaryUser) return;
    const parsed = salaryInput.trim() === '' ? null : Number(salaryInput);
    if (parsed !== null && (Number.isNaN(parsed) || parsed < 0)) {
      setError('Salary must be a positive number or empty');
      return;
    }
    setSavingSalary(true);
    try {
      await api.put(`/users/${salaryUser.id}`, { monthlySalary: parsed });
      setSalaryUser(null);
      setSalaryInput('');
      setSuccessMessage(`Monthly salary updated for ${salaryUser.firstName} ${salaryUser.lastName}`);
      await loadUsers();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save salary');
    } finally {
      setSavingSalary(false);
    }
  };

  const fmtMoney = (n: number | null | undefined) =>
    n == null ? '—' : n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

  const activeUsers = users.filter((u) => u.status === 'active');
  const inactiveUsers = users.filter((u) => u.status !== 'active');
  const displayedUsers = activeTab === 'active' ? activeUsers : inactiveUsers;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Users</h1>
          <p className="text-dark-400 mt-1">Manage team members and their access</p>
        </div>
        <button onClick={() => setIsAddingUser(true)} className="btn-primary">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add User
        </button>
      </div>

      {successMessage && (
        <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400 text-sm flex items-center gap-2">
          <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {successMessage}
        </div>
      )}

      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
          {error}
        </div>
      )}

      {isAddingUser && (
        <div className="card animate-slide-down">
          <h3 className="text-lg font-medium text-white mb-4">New User</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <input
              type="email"
              placeholder="Email"
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              className="input"
            />
            <input
              type="password"
              placeholder="Password"
              value={newUser.password}
              onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
              className="input"
            />
            <input
              type="text"
              placeholder="First Name"
              value={newUser.firstName}
              onChange={(e) => setNewUser({ ...newUser, firstName: e.target.value })}
              className="input"
            />
            <input
              type="text"
              placeholder="Last Name"
              value={newUser.lastName}
              onChange={(e) => setNewUser({ ...newUser, lastName: e.target.value })}
              className="input"
            />
            <select
              value={newUser.role}
              onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
              className="input"
            >
              <option value="employee">Employee</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className="flex gap-3">
            <button onClick={createUser} className="btn-primary">Create User</button>
            <button onClick={() => setIsAddingUser(false)} className="btn-ghost">Cancel</button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-dark-700">
        <div className="flex gap-6">
          <button
            onClick={() => setActiveTab('active')}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'active'
                ? 'border-primary-500 text-primary-400'
                : 'border-transparent text-dark-400 hover:text-dark-200'
            }`}
          >
            Active ({activeUsers.length})
          </button>
          <button
            onClick={() => setActiveTab('inactive')}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'inactive'
                ? 'border-primary-500 text-primary-400'
                : 'border-transparent text-dark-400 hover:text-dark-200'
            }`}
          >
            Inactive ({inactiveUsers.length})
          </button>
        </div>
      </div>

      <div className="card">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Role</th>
                {canSeeSalary && <th className="text-right">Monthly Salary</th>}
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayedUsers.length === 0 ? (
                <tr>
                  <td colSpan={canSeeSalary ? 5 : 4} className="text-center text-dark-400 py-8">
                    No {activeTab} users found
                  </td>
                </tr>
              ) : (
                displayedUsers.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center">
                          <span className="text-xs font-medium text-white">
                            {getInitials(user.firstName, user.lastName)}
                          </span>
                        </div>
                        <span className="font-medium text-dark-200">
                          {user.firstName} {user.lastName}
                        </span>
                      </div>
                    </td>
                    <td className="text-dark-400">{user.email}</td>
                    <td>
                      <span className={`badge ${user.role === 'admin' ? 'badge-primary' : 'badge-gray'}`}>
                        {user.role}
                      </span>
                    </td>
                    {canSeeSalary && (
                      <td className="text-right text-dark-200">
                        {fmtMoney((user as SafeUser & { monthlySalary?: number | null }).monthlySalary)}
                      </td>
                    )}
                    <td className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {activeTab === 'active' && canSeeSalary && (
                          <button
                            onClick={() => {
                              setSalaryUser(user);
                              const ms = (user as SafeUser & { monthlySalary?: number | null }).monthlySalary;
                              setSalaryInput(ms == null ? '' : String(ms));
                            }}
                            className="btn-sm bg-dark-600 hover:bg-dark-500 text-dark-200 rounded-lg px-3 py-1 text-sm transition-colors"
                            title="Set monthly salary (used for invoices)"
                          >
                            Set Salary
                          </button>
                        )}
                        {activeTab === 'active' && (
                          <>
                            <button
                              onClick={() => { setSetPasswordUser(user); setNewPassword(''); }}
                              className="btn-sm bg-dark-600 hover:bg-dark-500 text-dark-200 rounded-lg px-3 py-1 text-sm transition-colors"
                              title="Set password directly"
                            >
                              Set Password
                            </button>
                            <button
                              onClick={() => setResetConfirm(user)}
                              className="btn-sm btn-ghost"
                              title="Send password reset email"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                              </svg>
                              Email Reset
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => toggleUserStatus(user)}
                          className={`btn-sm ${activeTab === 'active' ? 'btn-danger' : 'btn-primary'}`}
                        >
                          {activeTab === 'active' ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Reset password confirmation modal */}
      {resetConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="card max-w-md w-full animate-slide-down">
            <h3 className="text-lg font-medium text-white mb-2">Reset Password</h3>
            <p className="text-dark-400 mb-1">
              Send a password reset link to:
            </p>
            <p className="text-white font-medium mb-4">
              {resetConfirm.email}
            </p>
            <p className="text-dark-500 text-sm mb-6">
              {resetConfirm.firstName} {resetConfirm.lastName} will receive an email with a link to set a new password. The link expires in 1 hour.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setResetConfirm(null)}
                className="btn-ghost"
                disabled={resetSending}
              >
                Cancel
              </button>
              <button
                onClick={() => sendResetEmail(resetConfirm)}
                className="btn-primary"
                disabled={resetSending}
              >
                {resetSending ? (
                  <>
                    <div className="spinner w-4 h-4" />
                    Sending...
                  </>
                ) : (
                  'Send Reset Link'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Set password modal */}
      {setPasswordUser && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="card max-w-md w-full animate-slide-down">
            <h3 className="text-lg font-medium text-white mb-2">Set Password</h3>
            <p className="text-dark-400 mb-4">
              Set a new password for <span className="text-white font-medium">{setPasswordUser.firstName} {setPasswordUser.lastName}</span>
            </p>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="New password (min 8 characters)"
              className="input w-full mb-2"
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter' && newPassword.length >= 8) setPassword(setPasswordUser); }}
            />
            <p className="text-dark-500 text-xs mb-6">
              This will immediately change their password and log them out of all sessions.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setSetPasswordUser(null); setNewPassword(''); }}
                className="btn-ghost"
                disabled={settingPassword}
              >
                Cancel
              </button>
              <button
                onClick={() => setPassword(setPasswordUser)}
                className="btn-primary"
                disabled={settingPassword || newPassword.length < 8}
              >
                {settingPassword ? (
                  <>
                    <div className="spinner w-4 h-4" />
                    Setting...
                  </>
                ) : (
                  'Set Password'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Set Salary modal */}
      {salaryUser && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="card max-w-md w-full animate-slide-down">
            <h3 className="text-lg font-medium text-white mb-2">Set Monthly Salary</h3>
            <p className="text-dark-400 mb-4">
              Set monthly salary for <span className="text-white font-medium">{salaryUser.firstName} {salaryUser.lastName}</span>
            </p>
            <label className="block text-sm text-dark-300 mb-1">Monthly salary (USD)</label>
            <input
              type="number"
              min="0"
              step="100"
              value={salaryInput}
              onChange={(e) => setSalaryInput(e.target.value)}
              placeholder="e.g. 8000"
              className="input w-full mb-2"
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') saveSalary(); }}
            />
            <p className="text-dark-500 text-xs mb-6">
              This amount is used as the total on the user's monthly invoice. Leave blank to skip generating invoices for this user. Salary is allocated proportionally across projects based on approved hours.
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => { setSalaryUser(null); setSalaryInput(''); }} className="btn-ghost" disabled={savingSalary}>Cancel</button>
              <button onClick={saveSalary} className="btn-primary" disabled={savingSalary}>
                {savingSalary ? <><div className="spinner w-4 h-4" /> Saving...</> : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
